import React from 'react';
import {
  addSignCommaFormat,
  addSignCommaFormatPercentage,
  commaFormat,
  getSign,
} from '../../utils/formatter';
import { convertEndOfTheMonth } from 'utils/date';
import {
  AssetChange as AssetChangeInternal,
  AssetChangeProps as AssetChangeProps_,
} from '../../components/AssetChange';
import { FREQUENCY, Frequency } from 'pages/NewReport';
import { yyyymmddToDate } from 'components/features/new-report/daily-report/DailyReport';
import { ReportBaseProps } from 'components/features/new-report/weekly-report/WeeklyReport';

export type AssetChangeProps = AssetChangeProps_;

type DateInformationArgs = {
  year: number;
  month: number;
  range: { from: number; to: number };
};

const getDateInformation = ({
  year,
  month,
  range,
}: DateInformationArgs): React.FC<Record<string, unknown>> => () => {
  const { from, to } = range;
  return (
    <div className="flex flex-col items-center flex-1">
      <div className="flex items-baseline mb-1 text-sm leading-tight">
        {year}年<div className="ml-1 text-2xl font-bold">{month}</div>
        <div className="ml-0.5 text-base font-bold">月</div>
      </div>
      <div className="text-sm">
        {from}日 〜 {to}日
      </div>
    </div>
  );
};

export const AssetChange = ({
  Base,
  CreatedAt,
  isItemMask,
  NextQuery,
  PreviousQuery,
  setQuery,
}: {
  Base: ReportBaseProps;
  CreatedAt: string | null;
  isItemMask: boolean;
  NextQuery: string | null;
  PreviousQuery: string | null;
  setQuery: (q: string, frequency: Frequency) => void;
}) => {
  const today = new Date();
  const from =
    yyyymmddToDate(Base.From.toString()) ||
    new Date(today.getFullYear(), today.getMonth(), 1);
  const to = yyyymmddToDate(Base.To.toString()) || convertEndOfTheMonth(today);

  const year = from.getFullYear();
  const month = from.getMonth() + 1;

  const totalAssetAmount =
    Base.TotalAssetAmount !== null ? commaFormat(Base.TotalAssetAmount) : null;
  const assetAmountChange =
    Base.DiffTotalAssetAmount !== null
      ? addSignCommaFormat(Base.DiffTotalAssetAmount)
      : null;
  const assetAmountChangeSign =
    Base.DiffTotalAssetAmount !== null
      ? getSign(Base.DiffTotalAssetAmount)
      : null;
  const comparisonToLastMonth =
    Base.DiffTotalAssetRate !== null
      ? addSignCommaFormatPercentage(Base.DiffTotalAssetRate, {
          numberOfDecimals: 2,
        })
      : null;

  const asset: AssetChangeProps['asset'] =
    totalAssetAmount !== null &&
    assetAmountChange !== null &&
    assetAmountChangeSign !== null &&
    comparisonToLastMonth !== null
      ? {
          totalAssetAmount,
          assetAmountChange,
          assetAmountChangeSign,
          comparisonToLast: comparisonToLastMonth,
        }
      : {
          totalAssetAmount: '---',
          assetAmountChange: '---',
          comparisonToLast: '---',
        };
  const createdAt = CreatedAt ? new Date(CreatedAt.replace(' ', 'T')) : null;
  const DateInformation = getDateInformation({
    year,
    month,
    range: {
      from: from.getDate(),
      to: to.getDate(),
    },
  });

  const goNext =
    NextQuery !== null
      ? (): void => {
          setQuery(NextQuery, FREQUENCY.MONTHLY);
        }
      : null;
  const goPrevious =
    PreviousQuery !== null
      ? (): void => {
          setQuery(PreviousQuery, FREQUENCY.MONTHLY);
        }
      : null;

  return (
    <AssetChangeInternal
      {...{
        DateInformation,
        updateInfoString: createdAt
          ? `${createdAt.getFullYear()}/${createdAt.getMonth() +
              1}/${createdAt.getDate()} ${createdAt
              .getHours()
              .toString()
              .padStart(2, '0')}:${createdAt
              .getMinutes()
              .toString()
              .padStart(2, '0')}`
          : null,
        asset,
        periodVerbose: '月',
        isItemMask,
        goNext,
        goPrevious,
      }}
    />
  );
};
